import React, { useEffect, useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import { makeStyles } from '@material-ui/core/styles'
import { useId } from 'hooks'
import CommonInput from '../CommonInput/CommonInput'
import { DateRangePickerType } from '../DateRangePicker/DateRangePicker'
import RangeCalendar from '../DateRangePicker/RangeCalendar'
import { isRangeAllowed, startOfDay } from '../DateRangePicker/dateRange'
import style from './style'

/**
 * Props for a controlled single-date picker using foundations input options.
 */
export interface DatePickerType extends Omit<DateRangePickerType, 'value' | 'onChange' | 'presets'> {
    value: Date | null
    onChange: (value: Date | null) => void
}

const useStyles = makeStyles(style)
const monthOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)

/**
 * A single-date field sharing the DateRangePicker calendar and palette styles.
 */
const DatePicker = ({
    value,
    onChange,
    color = 'neutral',
    minDate,
    maxDate,
    locale = 'en-GB',
    formatDate,
    placeholder = 'Select date',
    disabled = false,
    size = 'large',
    id,
    name,
    required,
    'aria-label': ariaLabel,
    'data-testid': dataTestId = 'date-picker',
    className,
    ...inputProps
}: DatePickerType): JSX.Element => {
    const generatedId = useId('date-picker')
    const fieldId = id || generatedId
    const classes = useStyles({ color })
    const [isOpen, setIsOpen] = useState(false)
    const [month, setMonth] = useState(monthOf(new Date()))
    const selected = value ? startOfDay(value) : null
    const range = selected ? { from: selected, to: selected } : null
    const format = formatDate || ((date: Date) => date.toLocaleDateString(locale, {
        day: 'numeric', month: 'long', year: 'numeric'
    }))

    useEffect(() => {
        if (disabled) setIsOpen(false)
    }, [disabled])

    const open = () => {
        if (disabled) return
        const today = startOfDay(new Date())
        const initialDate = minDate && today < startOfDay(minDate)
            ? minDate
            : (maxDate && today > startOfDay(maxDate) ? maxDate : today)
        setMonth(monthOf(value || initialDate))
        setIsOpen(true)
    }
    const selectDate = (date: Date) => {
        if (!isRangeAllowed({ from: date, to: date }, minDate, maxDate)) return
        onChange(startOfDay(date))
        setIsOpen(false)
    }

    return <>
        <CommonInput
            {...inputProps}
            value={value}
            disabled={disabled}
            size={size}
            id={fieldId}
            clearable={false}
            className={`${classes.input} ${className || ''}`}
            data-testid={dataTestId}
        >
            <input
                id={fieldId}
                name={name}
                required={required}
                className='text-field input'
                value={value ? format(value) : ''}
                readOnly
                disabled={disabled}
                placeholder={placeholder}
                aria-label={ariaLabel || inputProps.label || placeholder}
                aria-haspopup='dialog'
                aria-expanded={isOpen && !disabled}
                aria-controls={isOpen ? `${fieldId}-dialog` : undefined}
                data-testid={dataTestId}
                onClick={open}
                onKeyDown={event => {
                    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
                        event.preventDefault()
                        open()
                    }
                }}
            />
        </CommonInput>
        <Dialog
            open={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            maxWidth={false}
            classes={{ paper: classes.paper }}
            PaperProps={{ id: `${fieldId}-dialog`, 'aria-label': inputProps.label || 'Select date' }}
        >
            <div className={classes.layout}>
                <div className={classes.main}>
                    <RangeCalendar
                        label='Date'
                        month={month}
                        selected={selected}
                        range={range}
                        onMonthChange={setMonth}
                        onSelect={selectDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        locale={locale}
                        classes={classes}
                    />
                </div>
            </div>
        </Dialog>
    </>
}

export default DatePicker
